import { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Check, ChevronsUpDown, Search, X, ArrowRight, ArrowLeft, Loader2,
  PartyPopper, ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { TURKEY_DATA, PROVINCES } from "@/data/turkeyProvinces";
import { api } from "@/lib/api";

const BUSINESS_TYPES = ["Bireysel", "Şahıs şirketi", "Limited / Anonim şirket", "Oto yıkama işletmesi", "Mobil detailing işletmesi"];
const SERVICES = ["Dış yıkama", "İç + dış yıkama", "İç detaylı temizlik", "Koltuk temizliği", "Pasta-cila", "Seramik kaplama", "Motor temizliği", "Far temizliği / parlatma", "Diğer"];
const EXPERIENCE = ["Yeni başlıyorum", "1 yıldan az", "1–3 yıl", "3–5 yıl", "5+ yıl"];
const WATER_ELEC = [
  "Su ve elektrik ihtiyacımı kendim karşılayabilirim",
  "Suyu kendim karşılayabilirim, elektrik gerekir",
  "Elektriği kendim karşılayabilirim, su gerekir",
  "Müşteriden hem su hem elektrik gerekir",
];
const CAPACITY = ["1–2 araç", "3–5 araç", "6–10 araç", "10+ araç"];
const PAYMENTS = ["Nakit", "POS cihazı / kredi kartı", "Havale / EFT"];

const empty = {
  full_name: "", phone: "", email: "", province: "", districts: [], all_districts: false,
  business_type: "", mobile_service: "", services: [], other_service: "",
  experience: "", water_electricity: "", daily_capacity: "", payment_methods: [],
  profile_url: "", consent: false,
};

function formatPhone(v) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  const p = [d.slice(0, 4), d.slice(4, 7), d.slice(7, 9), d.slice(9, 11)].filter(Boolean);
  return p.join(" ");
}

const STEP_TITLES = ["İletişim Bilgileri", "Hizmet Detayları", "Operasyon & Onay"];

export const ApplicationForm = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [provOpen, setProvOpen] = useState(false);
  const [distOpen, setDistOpen] = useState(false);
  const cardRef = useRef(null);

  const scrollToFormTop = () => {
    requestAnimationFrame(() => {
      const el = cardRef.current;
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      if (window.__lenis) window.__lenis.scrollTo(y, { duration: 0.8 });
      else window.scrollTo({ top: y, behavior: "smooth" });
    });
  };

  const districts = useMemo(() => (data.province ? TURKEY_DATA[data.province] || [] : []), [data.province]);

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const toggleArray = (k, val) => {
    setData((d) => {
      const arr = d[k].includes(val) ? d[k].filter((x) => x !== val) : [...d[k], val];
      return { ...d, [k]: arr };
    });
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (data.full_name.trim().length < 2) e.full_name = "Lütfen ad soyad girin.";
      const digits = data.phone.replace(/\D/g, "");
      if (digits.length !== 11 || !digits.startsWith("0")) e.phone = "Geçerli bir telefon numarası girin (05XX XXX XX XX).";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) e.email = "Geçerli bir e-posta adresi girin.";
      if (!data.province) e.province = "Lütfen şehir seçin.";
      if (!data.all_districts && data.districts.length === 0) e.districts = "En az bir ilçe seçin veya tüm ilçeleri işaretleyin.";
    }
    if (s === 1) {
      if (!data.business_type) e.business_type = "Lütfen işletme türünü seçin.";
      if (!data.mobile_service) e.mobile_service = "Lütfen bir seçim yapın.";
      if (data.services.length === 0) e.services = "En az bir hizmet seçin.";
      if (data.services.includes("Diğer") && !data.other_service.trim()) e.other_service = "Lütfen diğer hizmeti belirtin.";
      if (!data.experience) e.experience = "Lütfen deneyim süresini seçin.";
    }
    if (s === 2) {
      if (!data.water_electricity) e.water_electricity = "Lütfen bir seçim yapın.";
      if (!data.daily_capacity) e.daily_capacity = "Lütfen günlük kapasiteyi seçin.";
      if (data.payment_methods.length === 0) e.payment_methods = "En az bir ödeme yöntemi seçin.";
      if (!data.consent) e.consent = "Devam etmek için onay vermelisiniz.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validateStep(step)) { setStep((s) => Math.min(s + 1, 2)); scrollToFormTop(); }
    else toast.error("Lütfen işaretli alanları kontrol edin.");
  };
  const prev = () => { setStep((s) => Math.max(s - 1, 0)); scrollToFormTop(); };

  const submit = async () => {
    if (!validateStep(2)) { toast.error("Lütfen işaretli alanları kontrol edin."); return; }
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { ...data, phone: data.phone };
      await api.post("/partner-applications", payload);
      setDone(true);
      window.scrollTo({ top: document.getElementById("basvuru")?.offsetTop - 60 || 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Başvuru gönderilemedi. Lütfen tekrar deneyin.");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => { setData(empty); setStep(0); setDone(false); setErrors({}); };

  return (
    <section id="basvuru" className="relative py-24 sm:py-32 bg-[#F7FAFC]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#22C7D6]/40 to-transparent" />
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal className="text-center">
          <div className="flex justify-center"><SectionLabel>Başvuru</SectionLabel></div>
          <h2 className="mt-5 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#172033]">
            YıkaGel Partner Başvurusu
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-[#667085]">
            Birkaç dakikada ön başvurunu tamamla. Başvurunu inceledikten sonra ekibimiz seninle iletişime geçsin.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div ref={cardRef} className="mt-12 rounded-[2rem] border border-gray-100 bg-white p-6 sm:p-10 shadow-[0_40px_80px_-50px_rgba(11,59,117,0.4)]">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="success"
                  data-testid="application-success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-8 text-center"
                >
                  <span className="mx-auto inline-flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-[#1677FF] to-[#22C7D6] text-white shadow-xl">
                    <PartyPopper size={34} />
                  </span>
                  <h3 className="mt-7 font-heading text-3xl font-bold text-[#172033]">Başvurun Alındı! 🎉</h3>
                  <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-[#667085]">
                    YıkaGel Partner Programı'na gösterdiğin ilgi için teşekkür ederiz. Ekibimiz başvurunu
                    inceleyerek en kısa sürede seninle iletişime geçecek.
                  </p>
                  <button
                    data-testid="back-home-btn"
                    onClick={() => { restart(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1677FF] px-7 py-3.5 text-base font-semibold text-white hover:bg-[#0B3B75] transition-colors"
                  >
                    Ana Sayfaya Dön
                  </button>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {/* Progress */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-[#1677FF]">Adım {step + 1} / 3</span>
                      <span className="text-[#667085]">{STEP_TITLES[step]}</span>
                    </div>
                    <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                      <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-[#1677FF] to-[#22C7D6]"
                        animate={{ width: `${((step + 1) / 3) * 100}%` }}
                        transition={{ duration: 0.4 }}
                      />
                    </div>
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-6"
                    >
                      {/* STEP 1 */}
                      {step === 0 && (
                        <>
                          <Field label="Adınız Soyadınız" error={errors.full_name} required>
                            <Input
                              data-testid="input-full-name"
                              value={data.full_name}
                              onChange={(e) => set("full_name", e.target.value)}
                              placeholder="Örn. Ahmet Yılmaz"
                              className="h-12 rounded-xl"
                            />
                          </Field>

                          <div className="grid sm:grid-cols-2 gap-6">
                            <Field label="Telefon Numaranız" error={errors.phone} required>
                              <Input
                                data-testid="input-phone"
                                inputMode="numeric"
                                value={data.phone}
                                onChange={(e) => set("phone", formatPhone(e.target.value))}
                                placeholder="05XX XXX XX XX"
                                className="h-12 rounded-xl"
                              />
                            </Field>
                            <Field label="E-posta Adresiniz" error={errors.email} required>
                              <Input
                                data-testid="input-email"
                                type="email"
                                value={data.email}
                                onChange={(e) => set("email", e.target.value)}
                                placeholder="ornek@eposta.com"
                                className="h-12 rounded-xl"
                              />
                            </Field>
                          </div>

                          {/* Province */}
                          <Field label="Hangi şehirde hizmet veriyorsunuz?" error={errors.province} required>
                            <Popover open={provOpen} onOpenChange={setProvOpen}>
                              <PopoverTrigger asChild>
                                <button
                                  data-testid="select-province"
                                  type="button"
                                  className="flex h-12 w-full items-center justify-between rounded-xl border border-input bg-white px-4 text-left text-[15px] hover:border-[#1677FF] transition-colors"
                                >
                                  <span className={data.province ? "text-[#172033]" : "text-[#98A2B3]"}>
                                    {data.province || "Şehir seçin"}
                                  </span>
                                  <ChevronsUpDown size={16} className="text-[#98A2B3]" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                                <Command>
                                  <CommandInput placeholder="Şehir ara..." />
                                  <CommandList className="thin-scroll">
                                    <CommandEmpty>Sonuç bulunamadı.</CommandEmpty>
                                    <CommandGroup>
                                      {PROVINCES.map((p) => (
                                        <CommandItem
                                          key={p}
                                          value={p}
                                          data-testid={`province-option-${p}`}
                                          onSelect={() => {
                                            set("province", p);
                                            set("districts", []);
                                            set("all_districts", false);
                                            setProvOpen(false);
                                          }}
                                        >
                                          <Check size={16} className={`mr-2 ${data.province === p ? "opacity-100 text-[#1677FF]" : "opacity-0"}`} />
                                          {p}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </Field>

                          {/* Districts */}
                          <Field label="Hangi ilçelerde hizmet verebilirsiniz?" error={errors.districts} required>
                            <div className="space-y-3">
                              <label className="flex items-center gap-3 rounded-xl border border-gray-100 bg-[#F7FAFC] px-4 py-3 cursor-pointer">
                                <Checkbox
                                  data-testid="checkbox-all-districts"
                                  checked={data.all_districts}
                                  onCheckedChange={(v) => { set("all_districts", !!v); if (v) set("districts", []); }}
                                />
                                <span className="text-[15px] font-medium text-[#172033]">Tüm ilçelerde hizmet verebilirim</span>
                              </label>

                              {!data.all_districts && (
                                <Popover open={distOpen} onOpenChange={setDistOpen}>
                                  <PopoverTrigger asChild>
                                    <button
                                      data-testid="select-districts"
                                      type="button"
                                      disabled={!data.province}
                                      className="flex min-h-12 w-full items-center justify-between rounded-xl border border-input bg-white px-4 py-2 text-left disabled:opacity-50 hover:border-[#1677FF] transition-colors"
                                    >
                                      <span className="flex flex-wrap gap-1.5">
                                        {data.districts.length === 0 ? (
                                          <span className="text-[15px] text-[#98A2B3]">
                                            {data.province ? "İlçe seçin" : "Önce şehir seçin"}
                                          </span>
                                        ) : (
                                          data.districts.map((d) => (
                                            <span key={d} className="inline-flex items-center gap-1 rounded-lg bg-[#1677FF]/8 px-2 py-1 text-xs font-medium text-[#1677FF]">
                                              {d}
                                              <X size={12} onClick={(e) => { e.stopPropagation(); toggleArray("districts", d); }} />
                                            </span>
                                          ))
                                        )}
                                      </span>
                                      <Search size={16} className="shrink-0 text-[#98A2B3]" />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                                    <Command>
                                      <CommandInput placeholder="İlçe ara..." />
                                      <CommandList className="thin-scroll">
                                        <CommandEmpty>Sonuç bulunamadı.</CommandEmpty>
                                        <CommandGroup>
                                          {districts.map((d) => (
                                            <CommandItem
                                              key={d}
                                              value={d}
                                              data-testid={`district-option-${d}`}
                                              onSelect={() => toggleArray("districts", d)}
                                            >
                                              <Check size={16} className={`mr-2 ${data.districts.includes(d) ? "opacity-100 text-[#1677FF]" : "opacity-0"}`} />
                                              {d}
                                            </CommandItem>
                                          ))}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                              )}
                            </div>
                          </Field>
                        </>
                      )}

                      {/* STEP 2 */}
                      {step === 1 && (
                        <>
                          <Field label="Nasıl hizmet veriyorsunuz?" error={errors.business_type} required>
                            <RadioGroup data-testid="radio-business-type" value={data.business_type} onValueChange={(v) => set("business_type", v)} className="grid sm:grid-cols-2 gap-3">
                              {BUSINESS_TYPES.map((o) => <OptionRadio key={o} value={o} label={o} name="bt" />)}
                            </RadioGroup>
                          </Field>

                          <Field label="Müşterinin bulunduğu konuma giderek hizmet verebilir misiniz?" error={errors.mobile_service} required>
                            <RadioGroup data-testid="radio-mobile-service" value={data.mobile_service} onValueChange={(v) => set("mobile_service", v)} className="grid grid-cols-2 gap-3">
                              {["Evet", "Hayır"].map((o) => <OptionRadio key={o} value={o} label={o} name="ms" />)}
                            </RadioGroup>
                          </Field>

                          <Field label="Hangi hizmetleri veriyorsunuz?" error={errors.services} required>
                            <div className="grid sm:grid-cols-2 gap-3">
                              {SERVICES.map((o) => (
                                <OptionCheck
                                  key={o}
                                  label={o}
                                  testid={`service-${o}`}
                                  checked={data.services.includes(o)}
                                  onChange={() => toggleArray("services", o)}
                                />
                              ))}
                            </div>
                            {data.services.includes("Diğer") && (
                              <div className="mt-3">
                                <Input
                                  data-testid="input-other-service"
                                  value={data.other_service}
                                  onChange={(e) => set("other_service", e.target.value)}
                                  placeholder="Verdiğiniz diğer hizmeti belirtin"
                                  className="h-12 rounded-xl"
                                />
                                {errors.other_service && <p className="mt-1.5 text-sm text-red-500">{errors.other_service}</p>}
                              </div>
                            )}
                          </Field>

                          <Field label="Kaç yıldır bu işi yapıyorsunuz?" error={errors.experience} required>
                            <RadioGroup data-testid="radio-experience" value={data.experience} onValueChange={(v) => set("experience", v)} className="grid sm:grid-cols-2 gap-3">
                              {EXPERIENCE.map((o) => <OptionRadio key={o} value={o} label={o} name="exp" />)}
                            </RadioGroup>
                          </Field>
                        </>
                      )}

                      {/* STEP 3 */}
                      {step === 2 && (
                        <>
                          <Field label="Yerinde hizmet verirken su ve elektrik ihtiyacınızı nasıl karşılıyorsunuz?" error={errors.water_electricity} required>
                            <RadioGroup data-testid="radio-water-elec" value={data.water_electricity} onValueChange={(v) => set("water_electricity", v)} className="grid gap-3">
                              {WATER_ELEC.map((o) => <OptionRadio key={o} value={o} label={o} name="we" />)}
                            </RadioGroup>
                          </Field>

                          <Field label="Günde yaklaşık kaç araca hizmet verebilirsiniz?" error={errors.daily_capacity} required>
                            <RadioGroup data-testid="radio-capacity" value={data.daily_capacity} onValueChange={(v) => set("daily_capacity", v)} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              {CAPACITY.map((o) => <OptionRadio key={o} value={o} label={o} name="cap" />)}
                            </RadioGroup>
                          </Field>

                          <Field label="Müşterilerinizden ödemeyi hangi yöntemlerle alabiliyorsunuz?" error={errors.payment_methods} required>
                            <div className="grid sm:grid-cols-3 gap-3">
                              {PAYMENTS.map((o) => (
                                <OptionCheck
                                  key={o}
                                  label={o}
                                  testid={`payment-${o}`}
                                  checked={data.payment_methods.includes(o)}
                                  onChange={() => toggleArray("payment_methods", o)}
                                />
                              ))}
                            </div>
                          </Field>

                          <Field label="Instagram veya işletme hesabınız" hint="Varsa Instagram, web sitesi veya işletme profilinizin bağlantısını paylaşabilirsiniz.">
                            <Input
                              data-testid="input-profile-url"
                              value={data.profile_url}
                              onChange={(e) => set("profile_url", e.target.value)}
                              placeholder="https://instagram.com/..."
                              className="h-12 rounded-xl"
                            />
                          </Field>

                          <div>
                            <label className="flex items-start gap-3 rounded-xl border border-gray-100 bg-[#F7FAFC] p-4 cursor-pointer">
                              <Checkbox
                                data-testid="checkbox-consent"
                                checked={data.consent}
                                onCheckedChange={(v) => set("consent", !!v)}
                                className="mt-0.5"
                              />
                              <span className="text-sm leading-relaxed text-[#344054]">
                                Kişisel verilerimin YıkaGel partner başvurumun değerlendirilmesi ve benimle iletişime
                                geçilmesi amacıyla işlenmesini kabul ediyorum.{" "}
                                <Link to="/kvkk" target="_blank" className="font-semibold text-[#1677FF] underline">KVKK Aydınlatma Metni</Link>
                                {" "}ve{" "}
                                <Link to="/gizlilik" target="_blank" className="font-semibold text-[#1677FF] underline">Gizlilik Politikası</Link>.
                              </span>
                            </label>
                            {errors.consent && <p data-testid="error-consent" className="mt-1.5 text-sm text-red-500">{errors.consent}</p>}
                          </div>
                        </>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {/* Nav buttons */}
                  <div className="mt-9 flex items-center justify-between gap-3">
                    {step > 0 ? (
                      <button
                        data-testid="form-prev"
                        onClick={prev}
                        className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-6 py-3.5 text-sm font-semibold text-[#172033] hover:border-[#1677FF] transition-colors"
                      >
                        <ArrowLeft size={16} /> Geri
                      </button>
                    ) : <span className="flex items-center gap-2 text-xs text-[#98A2B3]"><ShieldCheck size={14} className="text-[#22C7D6]" /> Bilgilerin güvende</span>}

                    {step < 2 ? (
                      <button
                        data-testid="form-next"
                        onClick={next}
                        className="inline-flex items-center gap-2 rounded-full bg-[#1677FF] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#0B3B75] transition-colors"
                      >
                        Devam Et <ArrowRight size={16} />
                      </button>
                    ) : (
                      <button
                        data-testid="submit-application"
                        onClick={submit}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 rounded-full bg-[#1677FF] px-8 py-3.5 text-sm font-semibold text-white hover:bg-[#0B3B75] transition-colors disabled:opacity-70"
                      >
                        {submitting ? <><Loader2 size={16} className="animate-spin" /> Gönderiliyor...</> : "Başvuruyu Gönder"}
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Field = ({ label, error, hint, required, children }) => (
  <div>
    <Label className="mb-2 block text-[15px] font-semibold text-[#172033]">
      {label} {required && <span className="text-[#1677FF]">*</span>}
    </Label>
    {hint && <p className="mb-2.5 text-sm text-[#667085]">{hint}</p>}
    {children}
    {error && <p className="mt-1.5 text-sm text-red-500">{error}</p>}
  </div>
);

const OptionRadio = ({ value, label }) => (
  <Label
    htmlFor={`r-${value}`}
    data-testid={`option-${value}`}
    className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 cursor-pointer transition-colors hover:border-[#1677FF] has-[button[data-state=checked]]:border-[#1677FF] has-[button[data-state=checked]]:bg-[#1677FF]/5"
  >
    <RadioGroupItem id={`r-${value}`} value={value} />
    <span className="text-[15px] text-[#172033]">{label}</span>
  </Label>
);

const OptionCheck = ({ label, checked, onChange, testid }) => (
  <label
    data-testid={testid}
    className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
      checked ? "border-[#1677FF] bg-[#1677FF]/5" : "border-gray-100 bg-white hover:border-[#1677FF]"
    }`}
  >
    <Checkbox checked={checked} onCheckedChange={onChange} />
    <span className="text-[15px] text-[#172033]">{label}</span>
  </label>
);
